import { map } from 'rxjs';
import { Injectable } from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, } from '@angular/common/http';
import { BehaviorSubject, catchError, filter, Observable, switchMap, take, throwError, } from 'rxjs';
import { AuthService } from '@shared/services';
import { environment } from 'src/environments/environment';
import { ELocalStorageKeys } from '@core/models/localStorage.model';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
	api_url = environment.api_url;
	private isRefreshing = false;
	private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);

	constructor(
		private authServices: AuthService
	) { }

	intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
		const token = localStorage.getItem(ELocalStorageKeys.accessToken);

		if (token && request.url.search('refresh-token') === -1) {
			if (this.expireSoonOrExpired(token, 60)) {
				return this.refreshAccessToken().pipe(
					switchMap((newToken: string | null) => {
						if (newToken) {
							request = request.clone({ setHeaders: { authorization: `Bearer ${newToken}`, }, });
							return next.handle(request);
						} else {
							localStorage.clear();
							const tenant = sessionStorage.getItem('tenant') || 'mex';
							const redirectTarget = `${environment.app_base_url}/page/${tenant}`;
							const registerUrl = `${environment.gateway}/login?redirect_uri=${encodeURIComponent(redirectTarget)}`;
							window.location.href = registerUrl;
							return throwError(() => new Error('No refresh token'));
						}
					})
				);
			} else {
				request = request.clone({ setHeaders: { authorization: `Bearer ${token}`, }, });
			}
		}

		return next.handle(request)
			.pipe(
				catchError((err: HttpErrorResponse) => {
					console.log(err);
					if (err.status === 401) {
						return this.handle401Error(request, next);
					}

					return throwError(() => err);
				})
			);
	}

	private expireSoonOrExpired(token: string, seconds: number): boolean {
		const jwtHelper = this.authServices.jwtHelper;
		if (this.authServices.isTokenExpired(token)) return true;
		const expirationDate = jwtHelper.getTokenExpirationDate(token);
		if (!expirationDate) return false;
		const now = new Date();
		const diff = (expirationDate.getTime() - now.getTime()) / 1000;
		return diff > 0 && diff <= seconds;
	}

	private handle401Error(request: HttpRequest<any>, next: HttpHandler) {
		let tenant = sessionStorage.getItem('tenant');
		if (!tenant) tenant = 'mex';
		const redirectTarget = `${environment.app_base_url}/page/${tenant}`;
		const registerUrl = `${environment.gateway}/login?redirect_uri=${encodeURIComponent(redirectTarget)}`;

		if (!this.isRefreshing) {
			this.isRefreshing = true;
			this.refreshTokenSubject.next(null);
			return this.refreshAccessToken().pipe(
				switchMap((newToken: string | null) => {
					if (newToken) {
						request = request.clone({ setHeaders: { authorization: `Bearer ${newToken}`, }, });
						this.isRefreshing = false;
						this.refreshTokenSubject.next(newToken);
						return next.handle(request);
					} else {
						localStorage.clear();
						this.isRefreshing = false;
						window.location.href = registerUrl;
						return throwError(() => new Error('No refresh token'));
					}
				}),
				catchError((err) => {
					if (err.status === 400) {
						localStorage.clear();
						this.isRefreshing = false;
						window.location.href = registerUrl;
					}
					return throwError(() => err);
				})
			);
		}

		return this.refreshTokenSubject.pipe(
			filter((token) => token !== null), take(1),
			switchMap((token: string) => {
				request = request.clone({ setHeaders: { authorization: `Bearer ${token}`, }, });
				return next.handle(request)
			})
		);
	}

	private refreshAccessToken(): Observable<string | null> {
		const refreshToken = localStorage.getItem(ELocalStorageKeys.refreshToken)!;
		if (!refreshToken) {
			return new Observable((observer) => {
				observer.next(null);
				observer.complete();
			});
		}
		return this.authServices.refreshToken(refreshToken).pipe(
			map((data: any) => {
				if (data && data.token) {
					localStorage.setItem(ELocalStorageKeys.accessToken, data.token);
					localStorage.setItem(ELocalStorageKeys.refreshToken, data.refreshToken);
					return data.token;
				}
				return null;
			})
		);
	}
}
